import { z } from 'zod'
import { sanitizeInput } from '@/utils/security'
import { emailSchema, nameSchema } from '@/utils/formSecurity'

const sanitizedRequiredString = (label: string, max = 200) =>
  z.string()
    .min(1, `${label} is required`)
    .max(max, `${label} must be less than ${max} characters`)
    .transform((value) => sanitizeInput(value))

const phoneSchema = z.string()
  .min(7, 'Phone number is required')
  .max(20, 'Phone number must be less than 20 characters')
  .regex(/^[0-9+()\-.\s]+$/, 'Enter a valid phone number')
  .transform((value) => sanitizeInput(value))

const numericString = (label: string) =>
  z.string()
    .min(1, `${label} is required`)
    .refine((value) => /^\d+$/.test(value), `${label} must be a valid number`)

export const applicationSchema = z.object({
  personal: z.object({
    firstName: nameSchema,
    lastName: nameSchema,
    email: emailSchema,
    phone: phoneSchema,
    nationality: sanitizedRequiredString('Nationality'),
    dob: sanitizedRequiredString('Date of birth'),
    gender: sanitizedRequiredString('Gender'),
    employmentStatus: sanitizedRequiredString('Employment status'),
    programme: sanitizedRequiredString('Programme'),
    cohort: sanitizedRequiredString('Cohort'),
    address: sanitizedRequiredString('Home address', 500),
    country: sanitizedRequiredString('Country'),
    state: sanitizedRequiredString('State / Province'),
    city: sanitizedRequiredString('City'),
    lga: sanitizedRequiredString('Local Government Area'),
  }),
  education: z.object({
    highestQualification: sanitizedRequiredString('Highest academic qualification'),
    hasOLevel: z.enum(['yes', 'no'], {
      required_error: "Please indicate if you have an O'Level certificate",
    }),
    oLevelTimes: numericString("Number of O'Level exam sittings"),
    oLevelTypes: z.array(sanitizedRequiredString("O'Level type")),
    wroteJamb: z.enum(['yes', 'no'], {
      required_error: 'Please indicate if you wrote JAMB exams',
    }),
    jambScore: z
      .string()
      .optional()
      .transform((value) => (value ? sanitizeInput(value) : value)),
  }).superRefine((data, ctx) => {
    if (data.hasOLevel === 'yes' && (!data.oLevelTypes || data.oLevelTypes.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select at least one O'Level type",
        path: ['oLevelTypes'],
      })
    }

    if (data.wroteJamb === 'yes') {
      if (!data.jambScore) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a valid JAMB score',
          path: ['jambScore'],
        })
        return
      }

      const score = Number(data.jambScore)
      if (Number.isNaN(score) || score <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a valid JAMB score',
          path: ['jambScore'],
        })
      }
    }
  }),
  payment: z.object({
    amount: z
      .string()
      .optional()
      .transform((value) => (value ? sanitizeInput(value) : value))
      .refine((value) => {
        if (!value) {
          return true
        }
        return /^\d+(\.\d{1,2})?$/.test(value.replace(/,/g, ''))
      }, 'Enter a valid amount'),
    reference: sanitizedRequiredString('Payment reference', 120),
  }),
})

export type ApplicationFormValues = z.infer<typeof applicationSchema>

export const APPLICATION_DEFAULT_VALUES: ApplicationFormValues = {
  personal: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    nationality: '',
    dob: '',
    gender: '',
    employmentStatus: '',
    programme: '',
    cohort: '',
    address: '',
    country: '',
    state: '',
    city: '',
    lga: '',
  },
  education: {
    highestQualification: '',
    hasOLevel: 'yes',
    oLevelTimes: '1',
    oLevelTypes: [],
    wroteJamb: 'no',
    jambScore: '',
  },
  payment: {
    amount: '',
    reference: '',
  },
}

export const GENDER_OPTIONS = ['Male', 'Female', 'Other'] as const
export const EMPLOYMENT_STATUS_OPTIONS = [
  'Employed',
  'Self-employed',
  'Unemployed',
  'Student',
] as const
export const QUALIFICATION_OPTIONS = [
  'High School Diploma',
  'ND',
  'HND',
  'Bachelors',
  'Masters',
  'Doctorate',
  'Other',
] as const
export const O_LEVEL_TYPE_OPTIONS = ['WASSCE', 'NECO', 'NABTEB', 'Others'] as const
